import React, { Fragment, useEffect, useState } from "react";
import { Flex } from "../../Flex";
import { PermissionType } from "../../../model/permissions/types/PermissionType";
import { useLocation } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../redux/store";
import { UserRoles } from "../../../model/user/types/UserRoles";
import { userSliceSelectors } from "../../../redux/user/user.selector";
import { taskSliceSelectors } from "../../../redux/task/task.selector";
import {
  Rate,
  Tooltip,
  Checkbox,
  GetProp,
  CheckboxOptionType,
} from "../../../utils/local-antd";
import { appSliceSelectors } from "../../../redux/app/app.selector";
import {
  BaseTask,
  ContactTask,
  LeadTask,
  OrphanTask,
} from "../../../model/task/Task";
import { useDrawersContext } from "../../../routes/tasks/drawers-context";
import { taskActions } from "../../../redux/task/task.slice";
import fetchAfterTaskAction from "../functions/fetchAfterTaskAction";
import { useFormContext } from "react-hook-form";
import { GetTasksFilterDto } from "../../../model/task/dto/GetTasksFilterDto";
import { AtiraToast } from "../../../utils/AtiraToast";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { AtiraCheckBoxGroup } from "../../AtiraCheckBoxGroup";
import { Spacing } from "../../../theme/Spacing";
import { AtiraImage } from "../../Image";
import { Button } from "../../Button";
import { Text } from "../../Text";
import { EntityAssignCheckbox } from "../../EntityAssignCheckbox";
import { standardDate } from "../../../utils/Date";
import { TaskKind } from "../../../model/task/shared/TasksKind.enum";
import { getTaskPriorityColor } from "../../../utils/taskUtils";
import { linkify } from "../../../utils/String";
import { getMediaUrl } from "../../../utils/Media";
import isNil from "lodash/isNil";
import { AtiraIcon } from "../../AtiraIcon";
import { faCircle } from "@fortawesome/free-solid-svg-icons/faCircle";
import { faPenToSquare } from "@fortawesome/free-solid-svg-icons/faPenToSquare";
import { faRotateLeft } from "@fortawesome/free-solid-svg-icons/faRotateLeft";
import { faCheck } from "@fortawesome/free-solid-svg-icons/faCheck";
import { faTrash } from "@fortawesome/free-solid-svg-icons/faTrash";
import isNumber from "lodash/isNumber";
import { Spinner } from "../../Spinner";
import { SpinnerFullScreen } from "../../SpinnerFullScreen";
import omit from "lodash/omit";
import isEmpty from "lodash/isEmpty";
import { AssigneeBanner } from "../../AssigneeBanner";

const StyledFlex = styled(Flex)`
  flex-wrap: wrap;
  gap: ${Spacing.s};
`;

const BoldText = styled(Text)`
  font-weight: 700;
`;

const ValueText = styled(Text)`
  word-break: break-word;
`;

const DeleteButton = styled(Button)`
  margin: 0;
  padding: 0;
  background-color: ${({ theme }) => theme.transparent};
  color: ${({ theme }) => theme.danger};
`;

const StyledButton = styled(Button)`
  padding: ${Spacing.s};
  margin: 0;
`;

const TaskImage = styled(AtiraImage)`
  max-height: 30rem;
  width: auto;
`;

const StyledAtiraCheckBoxGroup = styled(AtiraCheckBoxGroup)`
  flex-direction: column;
  gap: ${Spacing.s};
`;

type Props = {
  hideActions?: boolean;
  task: BaseTask;
};

export const TaskInfoTab: React.FC<Props> = ({ task, hideActions }) => {
  const [checkLoading, setCheckLoading] = useState(false);
  const [checkedBoxes, setCheckedBoxes] = useState<string[]>([]);
  const { pathname } = useLocation();
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const user = useAppSelector(userSliceSelectors.selectLoggedInUser);
  const taskSettings = useAppSelector(taskSliceSelectors.selectTasksSettings);
  const tasksPage = useAppSelector(taskSliceSelectors.selectTasksPage);
  const tasksPageSize = useAppSelector(taskSliceSelectors.selectTasksPageSize);
  const searchOptions = useAppSelector(appSliceSelectors.selectSearchOptions);
  const floatingTodayTasksVisible = useAppSelector(
    appSliceSelectors.floatingTodayTasksVisible,
  );

  const userPermissions = user?.permissions?.permissions;
  const upperTaskKind = task?.kind?.toUpperCase();
  const isTaskDone = task?.done === true;
  const canEdit =
    userPermissions?.[`${upperTaskKind}_TASK` as PermissionType]?.UPDATE;
  const canAssign = user?.roles?.includes(UserRoles.ORGANIZATION);
  const canDeleteTask =
    userPermissions?.[`${upperTaskKind}_TASK` as PermissionType]?.DELETE ===
    true;
  const isOnSearchPage = pathname.includes("/search");
  const showLoading = isEmpty(omit(task, ["_id", "kind"]));

  const {
    setEntry,
    setTask,
    setKanbanCard,
    setTaskUpdateVisible,
    setTaskDeleteVisible,
    setTaskDoneVisible,
    setTaskAssignVisible,
  } = useDrawersContext();
  const { getValues: contextGetValues } = useFormContext<GetTasksFilterDto>();

  const onOpenUpdateDrawer = () => {
    setTask(task!);
    setTaskUpdateVisible(true);
  };

  const onDeleteTask = () => {
    setTask(task!);
    setTaskDeleteVisible(true);
  };

  const onTaskDone = () => {
    setTask(task!);
    setTaskDoneVisible(true);
  };

  const onAssignTask = () => {
    setTask(task!);
    setTaskAssignVisible(true);
  };

  const getLeadTaskAttemptsOptions = () => {
    const leadTask = task as LeadTask;

    return Array.from(
      {
        length: Math.max(
          isNumber(taskSettings?.settings.LEAD.contactAttempts)
            ? taskSettings.settings.LEAD.contactAttempts
            : 3,
          leadTask?.contactAttempts!,
        ),
      },
      (_, i) => i + 1,
    ).map((item) => ({
      id: `${leadTask._id}-${item}`,
      label: (
        <Flex gap="s" alignItems="center">
          <Text
            style={{ whiteSpace: "nowrap" }}
          >{`${t("common.attempt")} ${item}`}</Text>
          {checkLoading && checkedBoxes.length === item ? (
            <Spinner color="main" size="1rem" />
          ) : null}
        </Flex>
      ),
      value: String(item),
      disabled:
        checkLoading ||
        checkedBoxes.includes(String(item)) ||
        item - (leadTask?.contactAttempts || 0) !== 1,
    })) as CheckboxOptionType[];
  };

  const getCheckedValues = (contactAttempts: number) => {
    if (contactAttempts === 0) {
      return [];
    }

    return Array.from({ length: contactAttempts }, (_, i) => i).map((item) =>
      String(item + 1),
    );
  };

  const incrementLeadTaskAttempt: GetProp<
    typeof Checkbox.Group,
    "onChange"
  > = async (checkedValues) => {
    const leadTask = task as LeadTask;

    try {
      setCheckLoading(true);
      setCheckedBoxes((prev) => [...prev, `${checkedValues.length - 1}`]);

      await dispatch(
        taskActions.incrementTaskContactAttempts({
          taskId: leadTask._id!,
          userId: user?._id!,
        }),
      ).unwrap();

      await fetchAfterTaskAction({
        userId: user?._id!,
        task,
        entryId: task?.parentId!,
        taskId: task?._id,
        pathname,
        tasksPage,
        tasksPageSize,
        dispatch,
        setEntry,
        setTask,
        setKanbanCard,
        contextGetValues,
        searchKeyword: searchOptions?.keyword,
        isFloatingTodayTasksOpen: floatingTodayTasksVisible === true,
      });

      AtiraToast.success(t("entries.contact_attempt.increment.success"));
    } catch (e: any) {
      AtiraToast.apiError(e);
      console.log(e);
      setCheckedBoxes((prev) => prev.slice(0, -1));
    } finally {
      setCheckLoading(false);
    }
  };

  useEffect(() => {
    setCheckedBoxes(getCheckedValues((task as LeadTask)?.contactAttempts!));
  }, [task]);

  if (showLoading) {
    /* See: onViewEntity() in utils/Reports.tsx */
    return <SpinnerFullScreen />;
  }

  return (
    <Flex flex={1} flexDirection="column" gap="m">
      <StyledFlex>
        <BoldText>{t("task.read.name")}</BoldText>
        <ValueText>{task?.name}</ValueText>
      </StyledFlex>

      {canAssign && task.assigneeIds?.length ? (
        <EntityAssignCheckbox value={task.assigneeIds} disabled />
      ) : null}

      {task?.reminder ? (
        <StyledFlex>
          <BoldText>{t("common.remind_date")}:</BoldText>
          <Text>{standardDate<string>(task.reminder, user, true)}</Text>
        </StyledFlex>
      ) : null}

      {task?.deadline ? (
        <StyledFlex>
          <BoldText>{t("common.deadline")}:</BoldText>
          <Text>{standardDate<string>(task.deadline, user, true)}</Text>
        </StyledFlex>
      ) : null}

      {[TaskKind.ORPHAN, TaskKind.CONTACT].includes(task.kind) ? (
        <StyledFlex>
          <BoldText>{t("common.priority")}:</BoldText>
          <Text
            style={{
              color: getTaskPriorityColor(
                (task as OrphanTask | ContactTask)?.priority,
              ),
            }}
          >
            {t(
              `common.${(task as OrphanTask | ContactTask)?.priority?.toLowerCase()}`,
            )}
          </Text>
        </StyledFlex>
      ) : null}

      {task?.description ? (
        <Flex gap="s" flexWrap="wrap">
          <BoldText>{t("common.description")}:</BoldText>
          <Flex>
            <Text
              wordBreak="break-word"
              dangerouslySetInnerHTML={{
                __html: linkify(task?.description),
              }}
            />
          </Flex>
        </Flex>
      ) : null}

      {task.kind === TaskKind.CONTACT ? (
        <Fragment>
          {(task as ContactTask)?.image ? (
            <Flex gap="s" flexDirection="column" flexWrap="wrap">
              <BoldText>{t("common.image")}:</BoldText>
              <TaskImage
                preview
                src={getMediaUrl((task as ContactTask).image!)}
              />
            </Flex>
          ) : null}

          {(task as ContactTask)?.rating ? (
            <Flex gap="s" flexDirection="column" flexWrap="wrap">
              <BoldText>{t("common.rating")}:</BoldText>
              <Rate count={10} disabled value={(task as ContactTask)?.rating} />
            </Flex>
          ) : null}
        </Fragment>
      ) : null}

      {task.kind === TaskKind.LEAD &&
      !isNil((task as LeadTask)?.contactAttempts) ? (
        <Flex flexDirection="column" gap="s">
          <StyledFlex>
            <Flex alignItems="center" gap="s">
              <AtiraIcon icon={faCircle} size="xs" color="main" />

              <BoldText>
                {t("entries.details.contact_attempts", {
                  count: (task as LeadTask).contactAttempts,
                })}
              </BoldText>
            </Flex>

            <Text fontSize="s" color="gray">
              {t("entries.details.contact_attempts.description")}
            </Text>

            <Text fontSize="s" color="gray">
              {t("entries.details.contact_attempts.update-description")}
            </Text>
          </StyledFlex>

          {canEdit ? (
            <StyledAtiraCheckBoxGroup
              onChange={incrementLeadTaskAttempt}
              options={getLeadTaskAttemptsOptions()}
              value={checkedBoxes}
              fontSize="1rem"
              key={task._id}
              disabled={hideActions || task.done}
            />
          ) : null}
        </Flex>
      ) : null}

      {!hideActions ? (
        <Flex justifyContent="space-between" alignItems="center" marginTop="m">
          <Flex gap="s" alignItems="center">
            {canEdit ? (
              <StyledButton
                onClick={onOpenUpdateDrawer}
                disabled={task.done === true}
                width="5rem"
              >
                {t("common.edit")}
              </StyledButton>
            ) : null}

            {canAssign ? (
              <StyledButton
                onClick={onAssignTask}
                icon={faPenToSquare}
                disabled={task.done}
              >
                {t("common.assign")}
              </StyledButton>
            ) : null}

            {canEdit ? (
              <Tooltip
                title={t(
                  isTaskDone ? "common.mark-undone" : "common.mark-done",
                )}
              >
                <StyledButton
                  icon={isTaskDone ? faRotateLeft : faCheck}
                  iconWidth="lg"
                  onClick={onTaskDone}
                />
              </Tooltip>
            ) : null}

            {isTaskDone ? (
              <Flex alignItems="center" gap="s">
                <Text fontSize="xs">
                  {t("common.closedBy", {
                    name: task.doneByUser?.name,
                    date: standardDate<string>(task.doneAt!, user, true),
                  })}
                </Text>
                {task.doneByUser ? (
                  <AssigneeBanner hideSelf assignees={[task.doneByUser]} />
                ) : null}
              </Flex>
            ) : null}
          </Flex>

          {canDeleteTask && !isOnSearchPage ? (
            <Tooltip title={t("common.delete")}>
              <DeleteButton
                icon={faTrash}
                iconWidth="2x"
                onClick={onDeleteTask}
              />
            </Tooltip>
          ) : null}
        </Flex>
      ) : null}
    </Flex>
  );
};
